export const convertToMinutes = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
};

/**
 * Returns the period name based on the game time in seconds
 * @param {number} seconds - total game seconds
 * @returns {string} - period name (1st, 2nd, 3rd, OT)
 */
export const getPeriodName = (seconds) => {
  const PERIOD_LENGTH = 20 * 60; // 20 minutes in seconds

  if (seconds < PERIOD_LENGTH) {
    return "1st";
  } else if (seconds < PERIOD_LENGTH * 2) {
    return "2nd";
  } else if (seconds < PERIOD_LENGTH * 3) {
    return "3rd";
  } else {
    return "OT"; // Overtime
  }
};

/**
 * Converts the total game time to time within the current period
 * @param {number} seconds - total game seconds
 * @returns {string} - formatted time within period (MM:SS)
 */
export const convertToPeriodTime = (seconds) => {
  const PERIOD_LENGTH = 20 * 60; // 20 minutes in seconds

  const periodSeconds = seconds % PERIOD_LENGTH;

  const periodTimeInSeconds = PERIOD_LENGTH - periodSeconds;

  const minutes = Math.floor(periodTimeInSeconds / 60);
  const remainingSeconds = periodTimeInSeconds % 60;

  return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
};

export const calculateLineupBoost = (lineup) => {
  console.log("------- lineup for boost calculation -------");
  let boost = 0;
  // Both Defenders from the same team (HockeyDefenseLeft = 26, HockeyDefenseRight = 27)
  const defenders = lineup.filter(
    (card) => card.playerPosition === 26 || card.playerPosition === 27
  );
  if (
    defenders.length === 2 &&
    defenders[0].teamExternalId === defenders[1].teamExternalId
  ) {
    console.log("Defenders from the same team +2 boost");
    boost += 2;
  }

  const ratings = lineup.map((card) => card.cardRating);
  const uniqueRatings = new Set(ratings);
  if (uniqueRatings.size >= 4) {
    console.log("Lineup consist 4 card ratings +5 boost");
    boost += 5;
  }

  const allGold = lineup.every((card) => card.cardRating === 2);
  if (allGold) {
    console.log("All cards GOLD +1 boost");
    boost += 1;
  }

  const allAllStar = lineup.every((card) => card.cardRating === 3);
  if (allAllStar) {
    console.log("All cards All-Star +2 boost");
    boost += 2;
  }
  return boost;
};

export const getPositionLetter = (position) => {
  switch (position) {
    case 0:
      return "T";
    case 1:
      return "D";
    case 2:
      return "M";
    case 3:
      return "F";
    case 4:
      return "G";
    case 5:
      return "G";
    case 6:
      return "F";
    case 7:
      return "C";
    case 13:
      return "C";
    case 14:
      return "D";
    case 15:
      return "RW";
    case 16:
      return "LW";
    default:
      return "?";
  }
};
const getTrainingAdditionalPoints = (trainingStat, seasonStats) => {
  const boost = Math.floor(trainingStat.TrainingCount / 5);
  switch (trainingStat.StatType) {
    case 0:
      const seasonGoals = seasonStats[2] ?? 0;
      return seasonGoals * boost;
    case 1:
      const seasonAssists = seasonStats[3] ?? 0;
      return seasonAssists * boost;
    case 2:
      const seasonShots = seasonStats[4] ?? 0;
      return seasonShots * boost;
    case 3:
      const seasonHits = seasonStats[5] ?? 0;
      return seasonHits * boost;
    case 4:
      const seasonBlockedShots = seasonStats[6] ?? 0;
      return seasonBlockedShots * boost;
    default:
      return 0;
  }
};
export const getLineupFpts = (lineup) => {
  const fpts = lineup.reduce((acc, card) => {
    if (
      !card ||
      !card.seasonStats ||
      !card.trainingStats ||
      card.team === null
    ) {
      console.warn("Card data is incomplete:", card);
      return acc;
    }

    const stats = card.seasonStats;
    let overallPoints = stats[1];
    for (const trainingStat of card.trainingStats) {
      overallPoints += getTrainingAdditionalPoints(trainingStat, stats);
    }
    const avgPoints =
      stats[0] > 0 ? Math.round((overallPoints / stats[0]) * 100) / 100 : 0;
    acc += avgPoints;

    return acc;
  }, 0);
  return fpts.toFixed(2);
};

export const getLineupScore = (lineup) => {
  const score = lineup.reduce((acc, card) => {
    acc += card.score ?? 0;
    return acc;
  }, 0);
  return score;
};

export const getCardAvgFpts = (card) => {
  let overallPoints = card.seasonStats[1];
  for (const trainingStat of card.trainingStats) {
    overallPoints += getTrainingAdditionalPoints(
      trainingStat,
      card.seasonStats
    );
  }
  const avgPoints =
    card.seasonStats[0] > 0
      ? Math.round((overallPoints / card.seasonStats[0]) * 100) / 100
      : 0;
  return avgPoints;
};

export const NHL_DIVISIONS = {
  EASTERN: {
    ATLANTIC: "Atlantic",
    METROPOLITAN: "Metropolitan",
  },
  WESTERN: {
    CENTRAL: "Central",
    PACIFIC: "Pacific",
  },
};

export const NHL_TEAMS_BY_DIVISION = {
  [NHL_DIVISIONS.EASTERN.ATLANTIC]: [
    "BOSTON BRUINS",
    "BUFFALO SABRES",
    "DETROIT RED WINGS",
    "FLORIDA PANTHERS",
    "MONTRÉAL CANADIENS",
    "OTTAWA SENATORS",
    "TAMPA BAY LIGHTNING",
    "TORONTO MAPLE LEAFS",
  ],
  [NHL_DIVISIONS.EASTERN.METROPOLITAN]: [
    "CAROLINA HURRICANES",
    "COLUMBUS BLUE JACKETS",
    "NEW JERSEY DEVILS",
    "NEW YORK ISLANDERS",
    "NEW YORK RANGERS",
    "PHILADELPHIA FLYERS",
    "PITTSBURGH PENGUINS",
    "WASHINGTON CAPITALS",
  ],
  [NHL_DIVISIONS.WESTERN.CENTRAL]: [
    "CHICAGO BLACKHAWKS",
    "COLORADO AVALANCHE",
    "DALLAS STARS",
    "MINNESOTA WILD",
    "NASHVILLE PREDATORS",
    "ST. LOUIS BLUES",
    "UTAH HOCKEY CLUB",
    "WINNIPEG JETS",
  ],
  [NHL_DIVISIONS.WESTERN.PACIFIC]: [
    "ANAHEIM DUCKS",
    "CALGARY FLAMES",
    "EDMONTON OILERS",
    "LOS ANGELES KINGS",
    "SAN JOSE SHARKS",
    "SEATTLE KRAKEN",
    "VANCOUVER CANUCKS",
    "VEGAS GOLDEN KNIGHTS",
  ],
};

export const NHL_TEAM_TO_DIVISION = (() => {
  const lookup = {};

  Object.entries(NHL_TEAMS_BY_DIVISION).forEach(([division, teams]) => {
    teams.forEach((team) => {
      lookup[team.toLowerCase()] = division;
      lookup[team.toLowerCase().replace(/\s+/g, "")] = division;

      const teamNameWords = team.toLowerCase().split(" ");
      if (teamNameWords.length > 1) {
        lookup[teamNameWords[teamNameWords.length - 1]] = division;
      }
    });
  });

  return lookup;
})();

export const getDivisionByTeam = (teamName) => {
  const normalized = teamName.toLowerCase().trim();
  return NHL_TEAM_TO_DIVISION[normalized] || null;
};
